import { FileCheck2, Package, Plane, Info, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/vgt";
import { Reveal, SectionHeader } from "./Reveal";

const POINTS = [
  {
    icon: Package,
    title: "Export & Vacuum Packaging",
    desc: "Our vanilla beans are double vacuum-sealed in food-grade foil pouches and packed in rigid master cartons to protect aroma and moisture in transit.",
  },
  {
    icon: FileCheck2,
    title: "Export Documentation",
    desc: "Where applicable, shipments are supported with a Phytosanitary Certificate, Certificate of Analysis, Commercial Invoice, and Packing List.",
  },
  {
    icon: Plane,
    title: "Shipping Assistance",
    desc: "We arrange express air freight and assist with shipping coordination to the USA, Europe, the Middle East, Asia, and Australia.",
  },
];

export const ExportShipping = () => (
  <section
    id="export-shipping"
    data-testid="export-shipping-section"
    className="py-20 sm:py-28 px-4 sm:px-8 bg-[#1C120C]"
  >
    <div className="max-w-7xl mx-auto">
      <SectionHeader
        dark
        eyebrow="04 · Export & Shipping"
        title="Export Ready, From Indonesia to Your Market"
        sub="Packaging, paperwork, and freight handled with an export-first mindset — so your order arrives the way it left."
      />
      <Reveal>
        <button
          data-testid="export-banner-button"
          onClick={() => scrollToId("quotation")}
          aria-label="Request a quotation"
          className="group block w-full mb-10 lg:mb-14 text-left"
        >
          <div className="overflow-hidden rounded-[2rem] border border-[#3D2B1E] shadow-2xl">
            <img
              data-testid="export-banner-image"
              src="/images/export-banner.jpg"
              alt="Export Ready, From Indonesia to Your Market — Vanilla Global Transindo"
              loading="lazy"
              className="w-full aspect-[16/9] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            />
          </div>
        </button>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        {POINTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div
              data-testid={`export-point-${i}`}
              className="h-full bg-[#FAF6EE]/5 border border-[#3D2B1E] rounded-3xl p-7 backdrop-blur-sm"
            >
              <span className="inline-flex w-12 h-12 rounded-2xl bg-[#D4A359]/15 text-[#D4A359] items-center justify-center">
                <p.icon size={21} />
              </span>
              <h3 className="mt-5 font-serif font-bold text-lg text-[#FAF6EE]">{p.title}</h3>
              <p className="mt-2.5 text-sm text-[#FAF6EE]/65 leading-relaxed">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div
          data-testid="shipping-note"
          className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-[#D4A359]/10 border border-[#D4A359]/30 rounded-2xl px-6 py-5"
        >
          <Info size={20} className="text-[#D4A359] shrink-0 mt-0.5" />
          <p className="text-sm text-[#FAF6EE]/80 leading-relaxed flex-1">
            Shipping costs are not included in product prices. Freight is quoted per destination together with
            your quotation, so you always see the full landed cost before confirming.
          </p>
          <button
            data-testid="export-cta-quote-button"
            onClick={() => scrollToId("quotation")}
            className="group shrink-0 inline-flex items-center gap-2 bg-[#D4A359] hover:bg-[#c2924a] text-[#1C120C] text-sm font-bold rounded-full px-5 py-2.5 transition-colors"
          >
            Get a Landed-Cost Quote
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);
